import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { FormikProps, FormikValues } from 'formik'

import { Alert, Progress, SimpleGrid, Text } from '@chakra-ui/react'
import {
  Button,
  Flex,
  Form,
  Icon,
  IconButton,
  Select,
  Spinner,
  Stack,
  Typography,
  useToast,
} from '@pnld/components-web'

import {
  requestPredictionReport,
  getPredictionReportStatus,
} from '@/api/predictions/services'
import SlidesDisplay from '@/components/Display/Slides'
import TotalizerDisplay from '@/components/Display/Totalizer'
import { ROUTES } from '@/config/routes'
import { Location, Region } from '@/types/dashboard'
import { getErrorDetails } from '@/utils/error'
import { formatDependency, formatLocation } from '@/utils/formatFilters'

import { usePredictionsController } from './controller'
import StagesTable from './Tables/Stages'
import { TStages } from './Tables/types'

type DownloadState = {
  jobId: string | null
  status: 'idle' | 'pending' | 'processing' | 'done' | 'failed'
  progressPct: number
  progressRows: number
  totalRows: number
  error: string | null
}

const INITIAL_DOWNLOAD_STATE: DownloadState = {
  jobId: null,
  status: 'idle',
  progressPct: 0,
  progressRows: 0,
  totalRows: 0,
  error: null,
}

const PredictionsResults = () => {
  const {
    predictionsPreview: controllerPreview,
    isPredictionsPreviewLoading,
    setPredictionsFilters,
    educationStages: educationStagesData,
  } = usePredictionsController()
  const toast = useToast()
  const navigate = useNavigate()
  const { state } = useLocation()
  const { filters, spheres, educationStages, location, dependency } = state

  const predictionsPreview = controllerPreview ??
    state.predictionsPreview ?? {
      qtCensus: 0,
      qtH1: 0,
      qtH2: 0,
      qtEntities: 0,
      teachingSteps: {},
    }

  const [completeDownload, setCompleteDownload] = useState<DownloadState>(
    INITIAL_DOWNLOAD_STATE
  )
  const [summarizedDownload, setSummarizedDownload] = useState<DownloadState>(
    INITIAL_DOWNLOAD_STATE
  )
  const [showDownloadSuccess, setShowDownloadSuccess] = useState(false)

  const pollingRef = useRef<{
    complete: ReturnType<typeof setInterval> | null
    summarized: ReturnType<typeof setInterval> | null
  }>({ complete: null, summarized: null })

  const triggerDownload = (url: string) => {
    const link = document.createElement('a')
    link.href = url
    document.body.appendChild(link)
    link.click()
    link.remove()
  }

  const startPolling = useCallback(
    (
      jobId: string,
      type: 'complete' | 'summarized',
      setter: React.Dispatch<React.SetStateAction<DownloadState>>
    ) => {
      const intervalId = setInterval(async () => {
        try {
          const status = await getPredictionReportStatus(jobId)

          setter(prev => ({
            ...prev,
            status: status.status.toLowerCase() as DownloadState['status'],
            progressPct: status.progressPct,
            progressRows: status.progressRows,
            totalRows: status.totalRows,
            error: status.error || null,
          }))

          if (status.status === 'DONE') {
            clearInterval(intervalId)
            pollingRef.current[type] = null

            const isMultiPart = (status.totalParts ?? 1) > 1

            if (isMultiPart) {
              status.downloadUrls.forEach((url, i) => {
                setTimeout(() => triggerDownload(url), i * 1000)
              })
              toast({
                status: 'info',
                title: `Relatório dividido em ${status.totalParts} arquivos`,
                description:
                  'O relatório excedeu o limite do Excel e foi dividido. Os downloads iniciarão em sequência.',
                isClosable: true,
                duration: 8000,
                position: 'top',
              })
            } else {
              triggerDownload(status.downloadUrl)
            }

            setShowDownloadSuccess(true)
            setTimeout(() => setShowDownloadSuccess(false), 6000)
          }

          if (status.status === 'FAILED') {
            clearInterval(intervalId)
            pollingRef.current[type] = null
            toast({
              status: 'error',
              title: 'Erro ao gerar relatório',
              description: status.error || 'Tente novamente.',
              isClosable: true,
            })
          }
        } catch (err) {
          clearInterval(intervalId)
          pollingRef.current[type] = null
          toast({
            status: 'error',
            title: 'Erro ao consultar status',
            description: getErrorDetails(err),
            isClosable: true,
          })
        }
      }, 3000)

      pollingRef.current[type] = intervalId
    },
    [toast]
  )

  useEffect(() => {
    return () => {
      if (pollingRef.current.complete)
        clearInterval(pollingRef.current.complete)
      if (pollingRef.current.summarized)
        clearInterval(pollingRef.current.summarized)
    }
  }, [])

  // ─── Download handlers ──────────────────────────────────────────────────────

  const handleRequestDownload = async (type: 'complete' | 'summarized') => {
    const setter =
      type === 'complete' ? setCompleteDownload : setSummarizedDownload

    setter({
      jobId: null,
      status: 'pending',
      progressPct: 0,
      progressRows: 0,
      totalRows: 0,
      error: null,
    })

    try {
      const { jobId } = await requestPredictionReport({
        ...filters,
        typePrediction: type,
      })

      setter(prev => ({ ...prev, jobId, status: 'processing' }))
      startPolling(jobId, type, setter)

      toast({
        status: 'info',
        title: 'Relatório em geração',
        description:
          'O arquivo está sendo processado em segundo plano. Você será notificado quando estiver pronto.',
        isClosable: true,
        duration: 6000,
        position: 'top',
      })
    } catch (err) {
      setter(INITIAL_DOWNLOAD_STATE)
      toast({
        status: 'error',
        title: 'Erro ao solicitar relatório',
        description: getErrorDetails(err),
        isClosable: true,
      })
    }
  }

  const zoneOptions = [
    { value: Location.RURAL.toString(), label: 'Rural' },
    { value: Location.URBAN.toString(), label: 'Urbana' },
  ]

  const regionOptions = [
    { value: Region.NORTH, label: 'Norte' },
    { value: Region.NORTHEAST, label: 'Nordeste' },
    { value: Region.MIDWEST, label: 'Centro-oeste' },
    { value: Region.SOUTHEAST, label: 'Sudeste' },
    { value: Region.SOUTH, label: 'Sul' },
  ]

  const slidesItems = useMemo(() => {
    const teachingStep = predictionsPreview?.teachingSteps
    const items = []

    const selectedStageCodes = filters.coEducationStageDisplay
      ? filters.coEducationStageDisplay.split(',')
      : []

    const snakeToCamel = (str: string) =>
      str
        .toLowerCase()
        .replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())

    const stageCodeToKeyMap: Record<string, string> = {}
    if (educationStagesData) {
      Object.entries(educationStagesData).forEach(([key, stages]) => {
        const camelKey = snakeToCamel(key)
        stages.forEach(stage => {
          stageCodeToKeyMap[String(stage.co_education_stage)] = camelKey
        })
      })
    }

    const selectedStageKeys =
      selectedStageCodes.length > 0
        ? Array.from(
            new Set(
              selectedStageCodes.map((code: string) => stageCodeToKeyMap[code])
            )
          )
        : []

    for (const stage in teachingStep) {
      if (selectedStageKeys.length > 0 && !selectedStageKeys.includes(stage)) {
        continue
      }
      const subTeachingSteps = teachingStep[stage].subTeachingSteps
      const stepMetrics = {
        census: teachingStep[stage].qtCensus,
        h1: teachingStep[stage].qtH1,
        h2: teachingStep[stage].qtH2,
      }
      const data = []
      for (const subStep in subTeachingSteps) {
        data.push({
          stage: subTeachingSteps[subStep].noEducationStage || '',
          census: subTeachingSteps[subStep].qtCensus,
          h1: subTeachingSteps[subStep].qtH1,
          h2: subTeachingSteps[subStep].qtH2,
          isTotal: false,
        })
      }
      data.push({ stage: '', ...stepMetrics, isTotal: true })
      items.push({
        title: 'Etapas de ensino',
        content: <StagesTable id={stage as TStages} data={data} />,
      })
    }
    return items
  }, [predictionsPreview, filters.coEducationStageDisplay, educationStagesData])

  const handleFilters = (values: FormikValues) => {
    setPredictionsFilters({ ...filters, ...values })
  }

  const formikRef = useRef<FormikProps<FormikValues>>(null)
  useEffect(() => {
    if (formikRef.current) {
      formikRef.current.setFieldValue('tpLocation', location)
    }
  }, [])

  const isCompleteLoading =
    completeDownload.status === 'pending' ||
    completeDownload.status === 'processing'
  const isSummarizedLoading =
    summarizedDownload.status === 'pending' ||
    summarizedDownload.status === 'processing'

  return (
    <Stack gap={6}>
      {showDownloadSuccess && (
        <Alert
          status="success"
          w="full"
          maxW="unset"
          gap={4}
          mt="0 !important"
          py="3xs"
        >
          <Flex justify="space-between" align="flex-start" w="full" gap={4}>
            <Flex gap={4} align="flex-start">
              <Icon name="check-circle" size="xs" variant="positive" />
              <Stack color="black">
                <Text fontWeight="bold">Predição baixada com sucesso!</Text>
                <Text mt="0 !important">
                  Verifique a pasta de destino de arquivos baixados do seu
                  computador.
                </Text>
              </Stack>
            </Flex>
            <IconButton
              iconName="close"
              variant="tertiary"
              size="xs"
              aria-label="Fechar"
              onClick={() => setShowDownloadSuccess(false)}
            />
          </Flex>
        </Alert>
      )}

      <Stack gap={4} p={4} boxShadow="md" borderRadius="base">
        <Typography variant="h-xs" color="brand.primary.dark_2">
          Predição - {filters.nuYearPrediction || filters.nuCensusYear}
        </Typography>
        <Flex direction="column">
          {/* <Typography variant="b-sm">
            <strong>Entidades Preditas:</strong>{' '}
            {predictionsPreview?.qtEntities}
          </Typography> */}
          <Typography variant="b-sm">
            <strong>Esfera:</strong> {spheres}
          </Typography>
          <Typography variant="b-sm">
            <strong>Etapa(s) de ensino:</strong> {educationStages}
          </Typography>
          {location && (
            <Typography variant="b-sm">
              <strong>Localização:</strong> {formatLocation(location)}
            </Typography>
          )}
          {dependency && (
            <Typography variant="b-sm">
              <strong>Dependência:</strong> {formatDependency(dependency)}
            </Typography>
          )}
        </Flex>

        {isCompleteLoading && (
          <Stack gap={1}>
            <Text fontSize="xs" color="brand.neutral.dark_2">
              Gerando predição completa...{' '}
              {completeDownload.progressPct > 0
                ? `${completeDownload.progressPct}%`
                : 'Aguarde. Isso pode levar alguns minutos.'}
            </Text>
            <Progress
              value={completeDownload.progressPct}
              size="sm"
              colorScheme="blue"
              borderRadius="md"
              isAnimated
              hasStripe
            />
          </Stack>
        )}
        {isSummarizedLoading && (
          <Stack gap={1}>
            <Text fontSize="xs" color="brand.neutral.dark_2">
              Gerando predição resumida...{' '}
              {summarizedDownload.progressPct > 0
                ? `${summarizedDownload.progressPct}%`
                : 'Aguarde. Isso pode levar alguns minutos.'}
            </Text>
            <Progress
              value={summarizedDownload.progressPct}
              size="sm"
              colorScheme="blue"
              borderRadius="md"
              isAnimated
              hasStripe
            />
          </Stack>
        )}

        <Flex justify="space-between" gap={4}>
          <Button
            label="Voltar"
            variant="tertiary"
            iconName="chevron-left"
            onClick={() => navigate(ROUTES.PREDICTIONS)}
          />
          <Flex gap={4}>
            <Button
              label="Baixar predição completa"
              variant="secondary"
              iconName="file-download"
              isLoading={isCompleteLoading}
              loadingText={
                completeDownload.progressPct > 0
                  ? `${completeDownload.progressPct}% concluído`
                  : 'Carregando...'
              }
              disabled={isCompleteLoading}
              onClick={() => handleRequestDownload('complete')}
            />
            <Button
              label="Baixar predição resumida"
              iconName="file-download"
              isLoading={isSummarizedLoading}
              loadingText={
                summarizedDownload.progressPct > 0
                  ? `${summarizedDownload.progressPct}% concluído`
                  : 'Carregando...'
              }
              disabled={isSummarizedLoading}
              onClick={() => handleRequestDownload('summarized')}
            />
          </Flex>
        </Flex>
      </Stack>

      <Stack gap={4} p={4} boxShadow="md" borderRadius="base">
        <Stack>
          <Typography variant="h-xs" color="brand.primary.dark_2">
            Pré-visualização de dados
          </Typography>
          <Typography variant="b-sm">
            Consulte abaixo totais de dados da predição solicitada.
          </Typography>
        </Stack>
        <Form onSubmit={handleFilters} innerRef={formikRef}>
          {({ setFieldValue }) => (
            <Flex gap={4} align="center">
              <Typography variant="b-sm" width="fit-content">
                Filtrar por
              </Typography>
              <Select
                name="tpLocation"
                options={zoneOptions}
                placeholder="Localização"
                {...{
                  value: zoneOptions.find(
                    option => option.value === filters.tpLocation
                  ),
                  onChange: (e: { value: string }) => {
                    setFieldValue('tpLocation', e.value)
                  },
                }}
              />
              <Select
                name="coRegion"
                options={regionOptions}
                placeholder="Região"
                isMulti
                disabled={!!filters.coUf}
              />
              <Flex>
                <Button
                  type="submit"
                  variant="secondary"
                  label="Filtrar"
                  iconName="fa-filter"
                />
              </Flex>
            </Flex>
          )}
        </Form>
        {isPredictionsPreviewLoading ? (
          <Flex justify="center" align="center" gap={4}>
            <Spinner color="brand.primary.dark_2" />
            <Typography variant="h-2xs">Carregando predições...</Typography>
          </Flex>
        ) : (
          <Stack gap={4}>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={4}>
              <TotalizerDisplay
                number={predictionsPreview?.qtCensus}
                label="Censo"
              />
              <TotalizerDisplay number={predictionsPreview?.qtH1} label="QT1" />
              <TotalizerDisplay number={predictionsPreview?.qtH2} label="QT2" />
              <TotalizerDisplay
                number={predictionsPreview?.qtEntities}
                label="Total de entidades"
                variant="highlight"
              />
            </SimpleGrid>
            {slidesItems.length === 0 && filters.coEducationStageDisplay ? (
              <Flex
                p={8}
                justify="center"
                align="center"
                borderRadius="md"
                bg="brand.neutral.light_3"
              >
                <Typography variant="b-sm" color="brand.neutral.dark_2">
                  Não há dados disponíveis para as etapas de ensino
                  selecionadas.
                </Typography>
              </Flex>
            ) : (
              <SlidesDisplay items={slidesItems} />
            )}
          </Stack>
        )}
      </Stack>
    </Stack>
  )
}

export default PredictionsResults
